import React, { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton } from '@material-ui/core';
import { BackButton, localization, getDimensionsOfPagination } from '../../sc-utilities';
import MaterialTable from 'material-table';
import * as actions from './store/errorLogActions';
import * as action from '../../np-core/Auth/store/authActions';
import { Refresh } from '@material-ui/icons';
import DataRange from '../../sc-utilities/DateRange';
import moment from 'moment';
import momentTimezone from 'moment-timezone';

const styles = theme => ({
    "@global": {

        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px",
        paddingTop: '4px'
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
        paddingTop: '4px'
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const ErrorLogList = props => {
    const { classes, translations, onFetchErrorLog, onFetchErrorLogCount, fetching, saveColumnsFilter, activeCustomer, tableColumns, tableRef } = props;
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const [dateRangeValue, setDateRangeValue] = useState(null);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        search: false,
        minBodyHeight: window.innerHeight - 259 < 300 ? 300 : window.innerHeight - 259,
        maxBodyHeight: window.innerHeight - 259,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        const customerTimezone = window.localStorage.getItem("CustomerTimezone") ?? 'UTC';

        const from = momentTimezone.tz(moment().subtract(7, 'days').set({
            hours: 0,
            minutes: 0,
            seconds: 0
        }), customerTimezone);

        const to = momentTimezone.tz(moment(), customerTimezone);
        
        setDateRangeValue({
            From: from,
            To: to,
            maxDate: to.set({
                hours: 23,
                minutes: 59,
                seconds: 59,
            }),
            query: {}
        });
        // eslint-disable-next-line
    }, [ activeCustomer ]);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        query['From'] = dateRangeValue?.From.format('YYYY-MM-DD');
        query['To'] = dateRangeValue?.To.format('YYYY-MM-DD');
        let count = await onFetchErrorLogCount(query);
        if (!query['page'] && !rowsPageSizeInit.includes(query['pageSize'])) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchErrorLog(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const filterDataRange = async (range) => {
        dateRangeValue.From = range.From
        dateRangeValue.To = range.To
        tableRef.current && tableRef.current.onQueryChange()
    };

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations["error_logs"] || 'Error logs'}</Typography>
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
            {dateRangeValue && <DataRange
                initialValues={dateRangeValue}
                filterDataRange={filterDataRange}
                value={dateRangeValue}
                setValue={setDateRangeValue}
                format="yyyy/MM/dd 00:00:00"
                translations={translations}>
            </DataRange>}
        </div>
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`ErrorLog`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer })
        return newColumnState
    }

    return (
        <Paper>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    setTableOptions({ ...tableOptions, pageSize: size })
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        fetching: state.admin.errorLog.fetching
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchErrorLogCount: filter => dispatch(actions.fetchErrorLogCount(filter)),
        onFetchErrorLog: filter => dispatch(actions.fetchErrorLog(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(ErrorLogList));